import React, { useState, useEffect } from 'react'
import { Icon, Alert, Box, Stack, Heading, Text, Button, SimpleTable, SimplePopover, Badge } from '@/components/lib'
import { FaArrowLeft, FaArrowRight, FaFileCsv } from 'react-icons/fa'

import api from '../../api'
import utils from '@/utils/utils'
import useStore from '../../store'

export default function TeamEvents () {
  const [page, setPage] = useState(1)
  const [logs, setLogs] = useState([])
  const [exporting, setExporting] = useState(false)
  const { team } = useStore()

  useEffect(() => {
    api.teams.getLogs(team._id, page, (data) => setLogs(data.logs))
  }, [team._id, page])

  function exportCSV () {
    setExporting(true)
    api.teams.getLogsCsv(team._id, ({ csv }) => {
      const safeName = (team?.name || 'team').replace(/ /g, '_')
      utils.downloadFile(`data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`, `${safeName}_events.csv`)
      utils.toast('success', 'CSV file generated successfully', 'It should now be in your downloads folder.')
      setExporting(false)
    }, err => {
      utils.toast('error', err.message || 'Unable to export logs')
      setExporting(false)
    })
  }

  return (
    <Stack gap={5}>
      <Box>
        <Heading as='h3' size='md'>Team event logs</Heading>
        <Text>For audit purposes, Trialflare logs key actions taken by members of your team.</Text>
      </Box>

      <Alert withIcon>You can find the logs relating to a specific trial on the trial's settings page.</Alert>

      <Stack gap={3}>
        <Box display='flex'>
          <Button
            size='sm'
            variant='outline'
            onClick={exportCSV}
            loading={exporting}
          >
            <Icon as={FaFileCsv} /> Export logs
          </Button>
        </Box>

        <Box>
          <SimpleTable
            tableProps={{ variant: 'simple' }}
            headers={['Date', 'User', 'Event', 'Details']}
            rows={logs.map(log => [
              utils.formatDateTime(log.createdAt),
              // Root entries are returned only to another root user; the badge
              // is a reminder that this row isn't one the team can see.
              <Stack direction='row' alignItems='center' gap={2} key={`${log._id}-user`}>
                <Text fontSize='sm'>{log.userEmail}</Text>
                {log.isRoot && <Badge colorPalette='purple'>Root</Badge>}
              </Stack>,
              log.type,
              <Stack direction='row' key={log._id}>
                {log.apiToken &&
                  <SimplePopover
                    key={`${log._id}-auth`}
                    on='hover'
                    trigger={<Badge>Auth</Badge>}
                    header='Authentication'
                    body={
                      <Box fontSize='xs'><pre>{JSON.stringify(log.apiToken, null, 2)}</pre></Box>
                    }
                  />}
                <SimplePopover
                  on='hover'
                  trigger={<Badge>Client</Badge>}
                  header='Client information'
                  body={
                    <Box whiteSpace='pre' fontSize='xs'><pre>{JSON.stringify(log.client, null, 2)}</pre></Box>
                  }
                />
                <SimplePopover
                  on='hover'
                  trigger={<Badge>Data</Badge>}
                  header='Data'
                  body={
                    <Box fontSize='xs'><pre>{JSON.stringify(log.event, null, 2)}</pre></Box>
                  }
                />
              </Stack>
            ])}
          />
        </Box>
      </Stack>

      <Box display='flex' justifyContent='space-between'>
        <Box>{page > 1 && <Button onClick={e => setPage(page - 1)}><Icon as={FaArrowLeft} mr={3} /> Previous page</Button>}</Box>
        <Box>{logs.length > 0 && <Button onClick={e => setPage(page + 1)}><Icon as={FaArrowRight} mr={3} /> Next page</Button>}</Box>
      </Box>
    </Stack>
  )
}
